import type { Theme } from '@mui/material';
import type { SystemStyleObject } from '@mui/system';

import type { Styles } from 'src/theme/themes';
import { isPositiveOrNegative } from 'src/components/dashboard/helper';

const styles = {
  barTableWidget: (theme) => ({
    flexGrow: 1,
    maxWidth: '100%',
    p: 2,

    '.MuiTableContainer-root': {
      // These styles are required for the scrollbar to be permanently displayed (Does not work for iOS).
      '&::-webkit-scrollbar': {
        WebkitAppearance: 'none',
        '&:horizontal': {
          height: 5,
        },
      },

      // These styles are required for the scrollbar to be permanently displayed (Does not work for iOS).
      '&::-webkit-scrollbar-thumb': {
        borderRadius: 2,
        backgroundColor: theme.colors.neutral[500],
      },
    },

    '.MuiTable-root': (theme) => ({
      borderCollapse: 'separate',

      th: {
        maxWidth: 0, // Necessary for Safari ('overflow-wrap: anywhere' can not be used in Safari.)
        height: 132,
        borderColor: theme.colors.neutral[100],
      },

      td: {
        minWidth: 100,
      },

      'th, td': {
        borderRight: `1px solid ${theme.colors.neutral[100]}`,

        '&:last-child': {
          borderRight: 'none',
        },
      },

      'tbody tr td': {
        borderBottom: 'none',
      },
    }),

    '.NumberDifference-root': (theme) => ({
      ...theme.typography.body2,
      display: 'flex',
      alignItems: 'center',
    }),
  }),

  firstColumn: (theme) => ({
    background: theme.palette.common.white,
    left: 0,
    position: 'sticky',
    zIndex: 1,
  }),

  firstColumnText: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    display: '-webkit-box', // 'WebkitLineClamp' does not work without "display: '-webkit-box'".
    WebkitLineClamp: '2',
  },

  rotateText: {
    transform: 'rotate(270deg)',
    maxHeight: 100,
    maxWidth: 100,
    margin: 'auto',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    display: '-webkit-box', // 'WebkitLineClamp' does not work without "display: '-webkit-box'".
    WebkitLineClamp: '2',
    overflowWrap: 'anywhere',
  },

  bar: (
    theme,
    comparison?: {
      comparisonCount: number | null | undefined;
      comparisonDiff: number | null | undefined;
    },
    color?: string,
    centeredScale?: boolean,
  ) => {
    const positiveOrNegative = isPositiveOrNegative(comparison?.comparisonDiff);
    const fallbackColor = positiveOrNegative
      ? positiveOrNegative > 0
        ? theme.colors.differencePositive
        : theme.colors.differenceNegative
      : theme.colors.differenceNeutral;

    return {
      backgroundColor: theme.colors.neutral[100],
      height: 22,
      display: 'flex',
      flexGrow: 1,
      position: 'relative',

      '&:after': {
        position: 'absolute',
        backgroundColor: color ?? fallbackColor,
        content: '""',
        width: comparison?.comparisonCount
          ? `${Math.abs(comparison.comparisonCount) / (centeredScale ? 2 : 1)}%`
          : undefined,
        display: 'block',
        height: '100%',
        top: 0,
        // If the scale is centered (npsScore), the bar should be centered as well.
        left:
          centeredScale &&
          comparison?.comparisonCount &&
          comparison.comparisonCount > 0
            ? '50%'
            : undefined,
        right:
          centeredScale &&
          comparison?.comparisonCount &&
          comparison.comparisonCount < 0
            ? '50%'
            : undefined,
      },
    } as SystemStyleObject<Theme>;
  },
} satisfies Styles & {
  bar: (
    theme: Theme,
    comparison?: {
      comparisonCount: number | null | undefined;
      comparisonDiff: number | null | undefined;
    },
    color?: string,
  ) => SystemStyleObject<Theme>;
};

export default styles;
